import { Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Briefcase, Globe, Calendar, MessageSquare, Phone, Mail, GripVertical, ClipboardEdit } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { ActivityDetail } from "@/types/activity";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";

interface ActivityKanbanCardProps {
  activity: ActivityDetail;
  index: number;
  onClick: () => void;
}

export function ActivityKanbanCard({ activity, index, onClick }: ActivityKanbanCardProps) {

  return (
    <Draggable draggableId={String(activity.id)} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={cn(
            "hover:shadow-md transition-all",
            snapshot.isDragging && "shadow-lg ring-2 ring-primary rotate-2"
          )}
        >
          <CardContent className="p-3 space-y-2">
            {/* Header com drag handle e nome */}
            <div className="flex items-start gap-2">
              <div
                {...provided.dragHandleProps}
                className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 text-muted-foreground/50 hover:text-muted-foreground"
              >
                <GripVertical className="h-4 w-4" />
              </div>
              <h4 className="font-semibold text-sm truncate flex-1">
                {activity.freelancer_nome}
              </h4>
            </div>

            {/* Email */}
            {activity.freelancer_email && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                <a
                  href={`mailto:${activity.freelancer_email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate hover:text-primary hover:underline"
                >
                  {activity.freelancer_email}
                </a>
              </div>
            )}

            {/* Telefone */}
            {activity.freelancer_telefone && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Phone className="h-3 w-3 shrink-0" />
                <a
                  href={`https://wa.me/55${activity.freelancer_telefone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="hover:text-green-600 hover:underline"
                >
                  {activity.freelancer_telefone}
                </a>
              </div>
            )}

            {/* Areas */}
            {activity.freelancer_areas && activity.freelancer_areas.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Briefcase className="h-3 w-3 shrink-0" />
                <span className="truncate">{activity.freelancer_areas.join(", ")}</span>
              </div>
            )}

            {/* Cidade */}
            {activity.freelancer_cidade && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0" />
                <span>{activity.freelancer_cidade}</span>
              </div>
            )}

            {/* Observacao */}
            {activity.observacao && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 p-1.5 rounded">
                <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="line-clamp-2">{activity.observacao}</span>
              </div>
            )}

            {/* Resumo do contato - exibido apenas quando concluído */}
            {activity.etapa_kanban === 'concluido' && activity.resumo && (
              <div className="flex items-start gap-1.5 text-xs bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-200 p-1.5 rounded border border-green-200 dark:border-green-800">
                <ClipboardEdit className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="line-clamp-3">{activity.resumo}</span>
              </div>
            )}

            {/* Footer: data + links */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>
                  {format(new Date(activity.created_at), "dd/MM", { locale: ptBR })}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <ExternalLinkButton
                  kind="portfolio"
                  url={activity.freelancer_portfolio_url}
                  className="h-auto w-auto p-1 hover:bg-slate-100 text-muted-foreground"
                  iconClassName="h-3.5 w-3.5"
                />
                <FreelancerLinkedInButton
                  freelancerId={activity.freelancer_id}
                  url={activity.freelancer_linkedin_url}
                  className="h-auto w-auto p-1 hover:bg-blue-100 text-blue-600"
                  iconClassName="h-3.5 w-3.5"
                />
              </div>
            </div>

            {/* Botao de registrar contato */}
            <Button
              size="sm"
              variant="outline"
              onClick={onClick}
              className="w-full h-7 text-xs gap-1.5"
            >
              <ClipboardEdit className="h-3.5 w-3.5" />
              Registrar Contato
            </Button>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
}
